// TradePrep runs as one app on two websites that share the same accounts and
// database: tradeprep.co.nz (New Zealand) and tradeprep.com.au (Australia). A
// "site" decides the things that differ: currency, language, time zone and
// the support address. Pure, so both the app and the Edge Functions use it.

export type SiteCode = 'nz' | 'au';

export interface Site {
  code: SiteCode;
  /** The main address, without www. */
  domain: string;
  /** ISO 3166 country code, e.g. the default country for a new provider. */
  country: 'NZ' | 'AU';
  /** The currency prices are shown and created in by default. */
  currency: 'NZD' | 'AUD';
  /** BCP 47 language tag for dates and money. */
  locale: 'en-NZ' | 'en-AU';
  timeZone: string;
  /** How times are labelled in emails, e.g. "11:30 am on 4 March 2027 (NZ time)". */
  timeLabel: string;
  supportEmail: string;
  /** Said to a learner on their dashboard. */
  greeting: string;
}

export const SITES: Record<SiteCode, Site> = {
  nz: {
    code: 'nz',
    domain: 'tradeprep.co.nz',
    country: 'NZ',
    currency: 'NZD',
    locale: 'en-NZ',
    timeZone: 'Pacific/Auckland',
    timeLabel: 'NZ time',
    supportEmail: 'support@tradeprep.co.nz',
    greeting: 'Kia ora',
  },
  au: {
    code: 'au',
    domain: 'tradeprep.com.au',
    country: 'AU',
    currency: 'AUD',
    locale: 'en-AU',
    timeZone: 'Australia/Sydney',
    timeLabel: 'Sydney time',
    supportEmail: 'support@tradeprep.com.au',
    greeting: "G'day",
  },
};

/** Anything that isn't recognised (localhost, a preview address, a missing value) is the New Zealand site. */
export const DEFAULT_SITE: SiteCode = 'nz';

/** `au` or `nz` from a stored value, otherwise the default. */
export function parseSiteCode(value: unknown): SiteCode {
  return value === 'au' || value === 'nz' ? value : DEFAULT_SITE;
}

/** The site a host name belongs to (www and other subdomains included), or null. */
export function siteFromHost(host: string | null | undefined): SiteCode | null {
  const h = (host ?? '').toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
  for (const site of Object.values(SITES)) {
    if (h === site.domain || h.endsWith(`.${site.domain}`)) return site.code;
  }
  return null;
}

/** The site a web address (or host) belongs to. */
export function siteForUrl(url: string | null | undefined): Site {
  let host = url ?? '';
  try {
    host = new URL(url ?? '').hostname;
  } catch {
    /* not a full address: treat it as a host name */
  }
  return SITES[siteFromHost(host) ?? DEFAULT_SITE];
}
